/* ============================================================
   Product Sample Inline (Gratis-Muster) — block: product_sample_inline
   Inline colour picker: select up to N variants, add them to cart.
   Earlier picks of the same product are replaced, so one cart holds
   max N samples from this picker. Cart refresh follows the same
   xoStore pattern as product-sample-popup.js / custom.js.
   ============================================================ */
(() => {
  'use strict';

  if (window.__psiLoaded) return;
  window.__psiLoaded = true;

  const SELECTOR = '[data-psi]';
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function getSectionIds() {
    const cartMini = document.querySelector('xo-cart-mini');
    if (cartMini && cartMini.id) return [cartMini.id, 'cart-icon-bubble'];
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function pushCartSections(sections, size) {
    if (typeof window.xoStore === 'undefined' || !sections || !Object.keys(sections).length) return;
    window.xoStore.set('xo-cart', function (prev) {
      return Object.assign({}, prev, {
        sections: sections,
        size: size != null ? size : (prev.size || 0)
      });
    })('xo-cart/updateCartSections');
  }

  function openCartDrawer() {
    const icon =
      document.querySelector(".xo-menu-hamburger__action-cart[xo-name='cart']") ||
      document.querySelector('.xo-menu-hamburger__action-cart');
    if (icon) icon.click();
  }

  async function getCart() {
    const res = await fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart.js HTTP ' + res.status);
    return res.json();
  }

  function formatMoney(cents) {
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
  }

  function init(root) {
    if (!root || root.dataset.psiReady === '1') return;
    root.dataset.psiReady = '1';

    const max = parseInt(root.dataset.max, 10) || 2;
    const productId = String(root.dataset.productId || '');
    const items = Array.from(root.querySelectorAll('[data-psi-item]'));
    const cta = root.querySelector('[data-psi-cta]');
    const ctaLabel = root.querySelector('[data-psi-cta-label]');
    const countEl = root.querySelector('[data-psi-count]');
    const priceEl = root.querySelector('[data-psi-price]');
    if (!cta || !ctaLabel || !items.length) return;

    const labels = {
      empty: root.dataset.labelEmpty || 'Wähle bis zu [max] Farben',
      ready: root.dataset.labelReady || '[count] Muster in den Warenkorb',
      loading: root.dataset.labelLoading || 'Wird hinzugefügt…',
      success: root.dataset.labelSuccess || 'Hinzugefügt',
      error: root.dataset.labelError || 'Fehler. Bitte erneut versuchen.',
      free: root.dataset.labelFree || 'Gratis'
    };

    const selected = []; // ordered variant ids (strings)
    let busy = false;

    const fill = (tpl, n) => String(tpl).replace(/\[count\]/g, n).replace(/\[max\]/g, max);

    function render() {
      const n = selected.length;

      items.forEach((item) => {
        const isOn = selected.indexOf(item.dataset.variantId) > -1;
        item.classList.toggle('is-selected', isOn);
        item.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        item.classList.toggle('is-maxed', !isOn && n >= max);
      });

      if (countEl) countEl.textContent = n;

      if (priceEl) {
        const total = selected.reduce((sum, id) => {
          const item = items.find((i) => i.dataset.variantId === id);
          return sum + (item ? parseInt(item.dataset.price || '0', 10) || 0 : 0);
        }, 0);
        priceEl.textContent = total > 0 ? formatMoney(total) : labels.free;
      }

      if (!busy) {
        cta.disabled = n === 0;
        ctaLabel.textContent = n === 0 ? fill(labels.empty, n) : fill(labels.ready, n);
      }
    }

    items.forEach((item) => {
      if (item.disabled) return;
      item.addEventListener('click', (e) => {
        e.preventDefault();
        if (busy) return;
        const id = item.dataset.variantId;
        const idx = selected.indexOf(id);
        if (idx > -1) {
          selected.splice(idx, 1);
        } else if (selected.length < max) {
          selected.push(id);
        } else {
          return; // max reached — deselect first
        }
        render();
      });
    });

    cta.addEventListener('click', async (e) => {
      e.preventDefault();
      if (busy || selected.length === 0) return;

      busy = true;
      cta.disabled = true;
      cta.classList.add('is-loading');
      ctaLabel.textContent = labels.loading;

      try {
        // 1) Replace earlier picks of this product (max N samples per cart)
        const before = await getCart();
        const updates = {};
        (before.items || []).forEach((line) => {
          if (String(line.product_id) === productId) updates[line.key] = 0;
        });
        if (Object.keys(updates).length) {
          const u = await fetch(routeRoot() + 'cart/update.js', {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify({ updates: updates })
          });
          if (!u.ok) throw new Error('cart/update.js HTTP ' + u.status);
        }

        // 2) Add the new selection (+ rendered cart sections)
        const res = await fetch(routeRoot() + 'cart/add.js', {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({
            items: selected.map((id) => ({ id: Number(id), quantity: 1 })),
            sections: getSectionIds(),
            section_url: window.location.pathname
          })
        });
        if (!res.ok) throw new Error('cart/add.js HTTP ' + res.status);
        const data = await res.json();

        // 3) Refresh mini-cart + bubble
        let size = null;
        try {
          size = (await getCart()).item_count;
        } catch (err) { /* keep previous size */ }
        pushCartSections(data.sections, size);
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(
          new CustomEvent('sampleInline:added', { bubbles: true, detail: { selectedIds: selected.slice() } })
        );

        cta.classList.remove('is-loading');
        cta.classList.add('is-success');
        ctaLabel.textContent = labels.success;
        openCartDrawer();

        setTimeout(() => {
          cta.classList.remove('is-success');
          busy = false;
          render();
        }, 2500);
      } catch (err) {
        console.error('[product-sample-inline]', err);
        cta.classList.remove('is-loading');
        ctaLabel.textContent = labels.error;
        busy = false;
        cta.disabled = false;
      }
    });

    render();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor: re-init when the section is re-rendered
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();